import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Button,
  Card,
  Checkbox,
  Flex,
  Input,
  Progress,
  Tag,
  Typography,
  Upload,
  theme,
  PaperClipOutlined,
  UploadOutlined,
} from '@/shared/antd-imports';
import { CheckCircleFilled, LockOutlined, PlayCircleOutlined, WarningOutlined } from '@ant-design/icons';

/** One checklist item as the client sees it (backend toClientView: no internal notes, no secrets). */
export interface IClientInput {
  id: string;
  item_type: 'invite_email' | 'username' | 'request_code' | 'file_upload' | 'question' | 'confirmation' | 'credential';
  service: string | null;
  title: string;
  instructions: string | null;
  video_url: string | null;
  identity_value: string | null;
  role_requested: string | null;
  required: boolean;
  due_date: string | null;
  status: 'requested' | 'submitted' | 'verified' | 'changes_requested' | 'revoked';
  client_value: string | null;
  has_secret: boolean;
  attachments: Array<{ id: string; name: string }>;
  submitted_at: string | null;
  change_note: string | null;
  can_submit: boolean;
}

export interface IClientInputsPage {
  project: { id: string; name: string };
  provider: { name: string };
  items: IClientInput[];
  summary: { total: number; open: number; submitted: number; verified: number; percent_done: number };
  uploads_enabled?: boolean;
}

export interface IInputSubmission {
  value?: string;
  secret?: string;
  confirmed?: boolean;
  files?: File[];
  confirm_not_secret?: boolean;
}

/** Thrown by `onSubmit` implementations so the form can show the server's message and code. */
export class InputSubmitError extends Error {
  code: string | null;
  constructor(message: string, code: string | null) {
    super(message);
    this.code = code;
  }
}

interface IInputsChecklistProps {
  page: IClientInputsPage;
  uploadsEnabled: boolean;
  onSubmit: (item: IClientInput, submission: IInputSubmission) => Promise<IClientInput>;
}

const STATUS_COLORS: Record<IClientInput['status'], string> = {
  requested: 'default',
  submitted: 'processing',
  verified: 'success',
  changes_requested: 'warning',
  revoked: 'default',
};

/**
 * "What we need from you": progress plus one card per item with short instructions and the right input
 * (Done tick, text, files, or a masked secret field). Shared by the portal page and the public magic-link page.
 */
export const InputsChecklist: React.FC<IInputsChecklistProps> = ({ page, uploadsEnabled, onSubmit }) => {
  const { t } = useTranslation();
  const [items, setItems] = useState<IClientInput[]>(page.items);
  const open = items.filter(item => item.status === 'requested' || item.status === 'changes_requested');
  const done = items.filter(item => item.status === 'submitted' || item.status === 'verified').length;
  const percent = items.length ? Math.round((done / items.length) * 100) : 0;

  return (
    <Flex vertical gap={16}>
      <Card>
        <Flex gap={24} align="center" wrap>
          <Progress type="circle" percent={percent} size={72} aria-label={t('inputs.progress', { defaultValue: 'Progress' })} />
          <Flex vertical gap={4} style={{ flex: '1 1 220px' }}>
            <Typography.Title level={4} style={{ margin: 0 }}>
              {t('inputs.title', { defaultValue: 'What we need from you' })}
            </Typography.Title>
            <Typography.Text type="secondary">
              {t('inputs.subtitle', {
                defaultValue: '{{provider}} needs these for {{project}}.',
                provider: page.provider.name,
                project: page.project.name,
              })}
            </Typography.Text>
            <Typography.Text>
              {open.length === 0
                ? t('inputs.allDone', { defaultValue: 'All done, thank you!' })
                : t('inputs.remaining', { defaultValue: '{{count}} item(s) left', count: open.length })}
            </Typography.Text>
          </Flex>
        </Flex>
      </Card>
      {items.length === 0 && (
        <Alert type="info" showIcon message={t('inputs.empty', { defaultValue: 'Nothing is needed from you right now.' })} />
      )}
      {items.map(item => (
        <InputCard
          key={item.id}
          item={item}
          uploadsEnabled={uploadsEnabled}
          onSubmit={async submission => {
            const updated = await onSubmit(item, submission);
            setItems(current => current.map(row => (row.id === updated.id ? updated : row)));
          }}
        />
      ))}
    </Flex>
  );
};

interface IInputCardProps {
  item: IClientInput;
  uploadsEnabled: boolean;
  onSubmit: (submission: IInputSubmission) => Promise<void>;
}

const InputCard: React.FC<IInputCardProps> = ({ item, uploadsEnabled, onSubmit }) => {
  const { t } = useTranslation();
  const { token } = theme.useToken();
  const [value, setValue] = useState(item.item_type === 'credential' ? '' : item.client_value ?? '');
  const [secret, setSecret] = useState('');
  const [confirmed, setConfirmed] = useState(false);
  const [files, setFiles] = useState<File[]>([]);
  const [isSaving, setSaving] = useState(false);
  const [error, setError] = useState<{ message: string; code: string | null } | null>(null);
  const [isEditing, setEditing] = useState(item.status === 'requested' || item.status === 'changes_requested');

  const statusLabel: Record<IClientInput['status'], string> = {
    requested: t('inputs.status.requested', { defaultValue: 'To do' }),
    submitted: t('inputs.status.submitted', { defaultValue: 'Sent, we will check it' }),
    verified: t('inputs.status.verified', { defaultValue: 'Done' }),
    changes_requested: t('inputs.status.changes_requested', { defaultValue: 'Please check again' }),
    revoked: t('inputs.status.revoked', { defaultValue: 'Closed' }),
  };

  const submit = async (confirmNotSecret = false) => {
    setSaving(true);
    setError(null);
    try {
      await onSubmit({
        value: value.trim() || undefined,
        secret: secret || undefined,
        confirmed: confirmed || undefined,
        files: files.length ? files : undefined,
        confirm_not_secret: confirmNotSecret || undefined,
      });
      setSecret('');
      setFiles([]);
      setConfirmed(false);
      setEditing(false);
    } catch (err) {
      const e = err as InputSubmitError;
      setError({ message: e.message, code: e.code ?? null });
    } finally {
      setSaving(false);
    }
  };

  const isDone = item.status === 'verified';
  const canSend =
    (item.item_type === 'invite_email' || item.item_type === 'confirmation') ? confirmed
      : item.item_type === 'credential' ? secret.length > 0
      : item.item_type === 'file_upload' ? files.length > 0 || value.trim().length > 0
      : value.trim().length > 0;

  return (
    <Card
      size="small"
      title={
        <Flex gap={8} align="center" wrap style={{ whiteSpace: 'normal', padding: '6px 0' }}>
          {isDone && <CheckCircleFilled style={{ color: token.colorSuccess }} />}
          <span>{item.title}</span>
          {!item.required && <Tag bordered={false}>{t('inputs.optional', { defaultValue: 'Optional' })}</Tag>}
        </Flex>
      }
      extra={<Tag color={STATUS_COLORS[item.status]}>{statusLabel[item.status]}</Tag>}
    >
      <Flex vertical gap={10}>
        {item.instructions && <Typography.Paragraph style={{ marginBottom: 0, whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{item.instructions}</Typography.Paragraph>}
        {item.identity_value && (
          <Typography.Text>
            {t('inputs.invite', { defaultValue: 'Invite' })}{' '}
            <Typography.Text code copyable={{ text: item.identity_value }} style={{ overflowWrap: 'anywhere' }}>{item.identity_value}</Typography.Text>
            {item.role_requested && (
              <Typography.Text type="secondary"> {t('inputs.asRole', { defaultValue: 'with the role: {{role}}', role: item.role_requested })}</Typography.Text>
            )}
          </Typography.Text>
        )}
        {!item.identity_value && item.role_requested && (
          <Typography.Text type="secondary">{t('inputs.roleOnly', { defaultValue: 'Access level: {{role}}', role: item.role_requested })}</Typography.Text>
        )}
        {item.video_url && (
          <a href={item.video_url} target="_blank" rel="noopener noreferrer">
            <PlayCircleOutlined /> {t('inputs.video', { defaultValue: 'Watch how to do it' })}
          </a>
        )}
        {item.due_date && (
          <Typography.Text type="secondary">{t('inputs.due', { defaultValue: 'Needed by {{date}}', date: new Date(`${item.due_date}T00:00:00`).toLocaleDateString() })}</Typography.Text>
        )}
        {item.status === 'changes_requested' && item.change_note && (
          <Alert type="warning" showIcon message={t('inputs.changeNote', { defaultValue: 'We need a change' })} description={item.change_note} />
        )}

        {!isEditing && (item.client_value || item.has_secret || item.attachments.length > 0) && (
          <div style={{ background: token.colorFillTertiary, borderRadius: token.borderRadius, padding: '6px 10px' }}>
            {item.client_value && item.item_type !== 'credential' && (
              <Typography.Paragraph style={{ marginBottom: 4, whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{item.client_value}</Typography.Paragraph>
            )}
            {item.has_secret && (
              <Typography.Text type="secondary">
                <LockOutlined /> {t('inputs.secretSaved', { defaultValue: 'Saved securely. It is hidden, even from you.' })}
              </Typography.Text>
            )}
            {item.attachments.map(file => (
              <div key={file.id}>
                <PaperClipOutlined /> {file.name}
              </div>
            ))}
          </div>
        )}

        {item.can_submit && !isEditing && item.status !== 'verified' && (
          <Button onClick={() => setEditing(true)} style={{ alignSelf: 'flex-start' }}>
            {t('inputs.change', { defaultValue: 'Change my answer' })}
          </Button>
        )}

        {item.can_submit && isEditing && (
          <Flex vertical gap={8}>
            {item.item_type === 'credential' && (
              <Alert
                type="info"
                showIcon
                icon={<LockOutlined />}
                message={t('inputs.credentialHint', {
                  defaultValue: 'If the service lets you invite a user instead, please do that. Otherwise enter a login made for us; it is encrypted and you can change it after the project.',
                })}
              />
            )}
            {(item.item_type === 'username' || item.item_type === 'request_code' || item.item_type === 'question' || item.item_type === 'credential') && (
              <Input.TextArea
                value={value}
                onChange={event => setValue(event.target.value)}
                autoSize={{ minRows: item.item_type === 'question' ? 3 : 1, maxRows: 8 }}
                maxLength={4000}
                placeholder={
                  item.item_type === 'credential'
                    ? t('inputs.loginPlaceholder', { defaultValue: 'Username or login URL (optional)' })
                    : item.item_type === 'request_code'
                      ? t('inputs.codePlaceholder', { defaultValue: 'Paste the code' })
                      : t('inputs.answerPlaceholder', { defaultValue: 'Your answer' })
                }
                aria-label={item.title}
              />
            )}
            {item.item_type === 'credential' && (
              <Input.Password
                value={secret}
                onChange={event => setSecret(event.target.value)}
                autoComplete="new-password"
                placeholder={t('inputs.secretPlaceholder', { defaultValue: 'Password or key' })}
                aria-label={t('inputs.secretPlaceholder', { defaultValue: 'Password or key' })}
              />
            )}
            {item.item_type === 'file_upload' && (
              <>
                {uploadsEnabled ? (
                  <Upload
                    multiple
                    maxCount={5}
                    beforeUpload={file => {
                      setFiles(current => [...current, file].slice(0, 5));
                      return false;
                    }}
                    onRemove={file => setFiles(current => current.filter(row => row.name !== file.name))}
                    fileList={files.map((file, index) => ({ uid: `${index}-${file.name}`, name: file.name, status: 'done' as const }))}
                  >
                    <Button icon={<UploadOutlined />}>{t('inputs.chooseFiles', { defaultValue: 'Choose files (up to 10 MB each)' })}</Button>
                  </Upload>
                ) : (
                  <Typography.Text type="secondary">
                    {t('inputs.uploadsOff', { defaultValue: 'Uploads are not available on this link. Paste a link to the files instead.' })}
                  </Typography.Text>
                )}
                <Input
                  value={value}
                  onChange={event => setValue(event.target.value)}
                  placeholder={t('inputs.linkPlaceholder', { defaultValue: 'Or paste a link (Google Drive, Dropbox…)' })}
                  aria-label={t('inputs.linkPlaceholder', { defaultValue: 'Or paste a link (Google Drive, Dropbox…)' })}
                />
              </>
            )}
            {(item.item_type === 'invite_email' || item.item_type === 'confirmation') && (
              <Checkbox checked={confirmed} onChange={event => setConfirmed(event.target.checked)}>
                {item.item_type === 'invite_email'
                  ? t('inputs.doneInvite', { defaultValue: 'I have sent the invite' })
                  : t('inputs.doneConfirm', { defaultValue: 'I confirm' })}
              </Checkbox>
            )}
            {error && (
              <Alert
                type={error.code === 'looks_like_secret' ? 'warning' : 'error'}
                showIcon
                icon={error.code === 'looks_like_secret' ? <WarningOutlined /> : undefined}
                message={
                  error.code === 'looks_like_secret'
                    ? t('inputs.looksLikeSecret', { defaultValue: 'This looks like a password. Please do not paste passwords here.' })
                    : error.message
                }
                action={
                  error.code === 'looks_like_secret' ? (
                    <Button size="small" onClick={() => submit(true)}>
                      {t('inputs.notSecret', { defaultValue: 'It is not a password' })}
                    </Button>
                  ) : undefined
                }
              />
            )}
            <Button type="primary" loading={isSaving} disabled={!canSend} onClick={() => submit(false)} style={{ alignSelf: 'flex-start' }}>
              {t('inputs.send', { defaultValue: 'Send' })}
            </Button>
          </Flex>
        )}
      </Flex>
    </Card>
  );
};

export default InputsChecklist;
